/**
 * Dünner Web-Audio-Adapter.
 *
 * Dies ist die **einzige** Stelle der Audio-Schicht mit direktem Bezug zur
 * Web Audio API. Die übrige Logik (Timing, Planung, Transport) arbeitet gegen
 * das {@link AudioEngine}-Interface und ist dadurch ohne Browser testbar.
 *
 * Der AudioContext wird bewusst **erst bei der ersten Benutzeraktion** erzeugt
 * bzw. fortgesetzt, um die Autoplay-Sperren moderner Browser (insbesondere
 * Safari/iOS) korrekt zu bedienen.
 */

import type { SampleDecoder } from './samples';

export type AudioEngineState = 'suspended' | 'running' | 'closed';

/** Abstraktion über die für die Wiedergabe benötigten Audiofähigkeiten. */
export interface AudioEngine extends SampleDecoder {
  /** Aktuelle Kontextzeit in Sekunden (maßgebliche Zeitbasis). */
  readonly currentTime: number;
  readonly state: AudioEngineState;
  /** Erzeugt/aktiviert den Kontext (nur aus einer Benutzeraktion aufrufen). */
  resume(): Promise<void>;
  /** Plant die Wiedergabe eines Buffers zu einem Kontextzeitpunkt. */
  play(buffer: AudioBuffer, atTime: number, gain: number): void;
  /** Stoppt alle geplanten bzw. laufenden Klänge unmittelbar. */
  stopAll(): void;
}

type AudioContextCtor = new () => AudioContext;

function resolveAudioContextCtor(): AudioContextCtor {
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  const ctor = w.AudioContext ?? w.webkitAudioContext;
  if (!ctor) {
    throw new Error('Web Audio API wird von diesem Browser nicht unterstützt.');
  }
  return ctor;
}

export class WebAudioEngine implements AudioEngine {
  #ctx: AudioContext | null = null;
  #master: GainNode | null = null;
  readonly #active = new Set<AudioBufferSourceNode>();

  #ensureContext(): AudioContext {
    if (this.#ctx) {
      return this.#ctx;
    }
    const Ctor = resolveAudioContextCtor();
    const ctx = new Ctor();

    // Summenpegel begrenzen, damit gleichzeitige Schläge nicht übersteuern.
    const master = ctx.createGain();
    master.gain.value = 0.85;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.12;

    master.connect(limiter).connect(ctx.destination);

    this.#ctx = ctx;
    this.#master = master;
    return ctx;
  }

  get currentTime(): number {
    return this.#ctx?.currentTime ?? 0;
  }

  get state(): AudioEngineState {
    return (this.#ctx?.state ?? 'suspended') as AudioEngineState;
  }

  async resume(): Promise<void> {
    const ctx = this.#ensureContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
  }

  async fetch(url: string): Promise<ArrayBuffer> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Sample-Abruf fehlgeschlagen (${response.status}): ${url}`,
      );
    }
    return response.arrayBuffer();
  }

  decode(data: ArrayBuffer): Promise<AudioBuffer> {
    return this.#ensureContext().decodeAudioData(data);
  }

  play(buffer: AudioBuffer, atTime: number, gain: number): void {
    const ctx = this.#ensureContext();
    const master = this.#master;
    if (!master) {
      return;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gainNode = ctx.createGain();
    gainNode.gain.value = gain;
    source.connect(gainNode).connect(master);
    source.onended = () => {
      this.#active.delete(source);
    };
    this.#active.add(source);
    source.start(Math.max(atTime, ctx.currentTime));
  }

  stopAll(): void {
    for (const source of this.#active) {
      try {
        source.onended = null;
        source.stop();
      } catch {
        // Bereits beendete Quellen ignorieren.
      }
    }
    this.#active.clear();
  }
}
